// React imports
import React, { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

//Child imports
import { ArrowRight, Check, Globe, Plus, Radio } from 'react-feather'

// Util imports
import { alertSeverites, apiURI } from 'src/lib/utils/constants'
import { putRequest } from 'src/lib/core/api-client'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import useSWR, { mutate } from 'swr'
import { channelListAdapter } from '../../../lib/utils/adapters'
import { Button, Typography } from '@mui/material'
import ViewChannelDetails from './ViewChannelDetails'
import FloGenericDialog from '../../common/feedback-and-dialog/GenericDialog'
import InputTextField from '../../common/form-elements/InputTextField'
import { ChannelCreationFormErrorState, ChannelDetailsType } from '../../../lib/types/settings'
import { initialFormErrorState } from '../../../assets/SettingsConfig'
import { defaultErrorState } from 'src/assets/DiscountsConfig'
import ChannelCreationForm from './ChannelCreationForm'
import { t } from 'i18next'
import { createChannel } from 'src/lib/core/api-methods'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { parseChannelDetails } from 'src/lib/utils/parsers'
import Permission from 'src/services/PermissionService'
import { permissionType } from 'src/lib/utils/constants'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import GenericTable from 'src/components/common/table/GenericTable'
import { useActivityLogs, EntityName } from 'src/lib/hooks/useActivityLogs'
import useGTM from 'src/lib/hooks/useGTM'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ChannelsProps {}

const Channels: React.FC<ChannelsProps> = ({}) => {
  const { sendActivityLog } = useActivityLogs()
  const router = useRouter()
  const { setting_id, s_id: channelId } = router.query
  const { setLoaderProperties } = useLoader()
  const { sendGTMTrigger } = useGTM()
  const [totalRowCount, setTotalRowCount] = useState<number>(0)
  const [rows, setRows] = useState<any>()
  const [isCreateChannelDialogOpen, setIsCreateChannelDialogOpen] = useState<boolean>(false)
  const [channelDetails, setChannelDetails] = useState<ChannelDetailsType>()
  const [formErrors, setFormErrors] = useState<ChannelCreationFormErrorState>(initialFormErrorState)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  })
  const newrowsPerPage = pagination.pageSize
  const newpage = pagination.pageIndex

  const shouldFetchChannelListData = useCallback(() => {
    if (Boolean(setting_id?.[1])) return false
    return true
  }, [setting_id])

  const { data: channelsResponse, isValidating } = useSWR(
    shouldFetchChannelListData()
      ? [`/merchant-app?offset=${newpage + 1}&limit=${newrowsPerPage}`, apiURI.AUTH_ENGINE]
      : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  useEffect(() => {
    if (!Boolean(channelsResponse) || Boolean(isValidating)) return
    const dataToShow = (channelsResponse as any)?.data?.map((rowData: any) => {
      return channelListAdapter(rowData)
    })
    setTotalRowCount((channelsResponse as any)?.total_count)
    setRows(dataToShow)
  }, [channelsResponse, isValidating])

  const columns: ColumnDef<any>[] = [
    {
      header: 'Channel name',
      cell: ({ row }: { row: any }) => {
        const value = row.original['channelName']
        return (
          <div className='flex items-center h-full gap-2'>
            <Globe className='w-4 h-4 stroke-[2.5px] text-coal-dark' />
            <span className='text-sm font-medium text-coal-dark'>{value}</span>
          </div>
        )
      }
    },
    {
      accessorKey: 'id',
      header: () => <div className='text-right mx-16 w-full'>Status</div>,
      cell: ({ row }: { row: any }) => {
        const value = row.original['id']
        return (
          <div className='flex items-center justify-end w-full h-[32px] gap-2'>
            <div
              className={`${
                Boolean(value) ? 'bg-[#01CCA733] text-primary-dark' : 'bg-gray-lightest text-yellow-dark'
              } rounded-lg gap-1 flex items-center px-[6px]`}
            >
              <Check className={`w-4 h-4 text-primary-dark stroke-[2px]`} />
              <span className={` `}>{Boolean(value) ? 'Active' : 'Inactive'}</span>
            </div>
            <ArrowRight className={`w-5 h-5 text-coal-dark stroke-[2px]`} />
          </div>
        )
      }
    }
  ]

  const onRowClick = (params: any) => {
    router.push(`/settings/channels?s_id=${params?.id}`)
  }

  const isValidForm = () => {
    let errorFound = false

    if (!Boolean(channelDetails?.name?.trim()?.length)) {
      setFormErrors((oldValues: ChannelCreationFormErrorState) => ({
        ...oldValues,
        ...{ name: { status: true, errorCode: '' } }
      }))
      errorFound = true
    }
    return !errorFound
  }

  const handleCreateChannel = async () => {
    if (!isValidForm()) return
    const createChannelPayload = {
      name: channelDetails?.name,
      description: channelDetails?.description
    }
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const channelCreationResponse: any = await createChannel(createChannelPayload)
      if (channelCreationResponse?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, channelCreationResponse?.error)
        console.error('x-trace-id:', channelCreationResponse?.headers?.['x-trace-id'])
        setLoaderProperties({ isLoaderVisible: false })
        return
      }
      const activityLogPayload = {
        entityName: EntityName.SETTINGS,
        actionName: 'edited Setup'
      }
      sendActivityLog(activityLogPayload)
      setLoaderProperties({ isLoaderVisible: false })
      sendGTMTrigger('channel_created', 'create')
      router.push(
        {
          pathname: `/settings/channels`,
          query: parseChannelDetails(channelCreationResponse?.data?.data)
        },
        `/settings/channels?s_id=${channelCreationResponse?.data?.data?.id}`
      )
    } catch (e) {
      setLoaderProperties({ isLoaderVisible: false })
      console.error(e)
      handleAlerts(alertSeverites.SEVERITY_ERROR, t('something_went_wrong'))
    }
  }

  const renderCreateChannelDialogContent = () => {
    return (
      <ChannelCreationForm
        channelDetails={channelDetails}
        setChannelDetails={setChannelDetails}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
      />
    )
  }

  const renderChannelsList = useCallback(() => {
    if (Boolean(setting_id?.length === 1 && !channelId)) {
      return (
        <div>
          <Permission permissionId={permissionType.CHANNEL} quantity={rows?.length}>
            <Button
              variant='contained'
              size='small'
              sx={{ padding: 0, width: 'fit-content', position: 'absolute', right: 60, top: 90 }}
              onClick={() => {
                setIsCreateChannelDialogOpen(true)
              }}
            >
              <Plus className='w-4 h-4 mr-1 stroke-[2.5px]' />
              Add channel
            </Button>
          </Permission>
          <GenericTable
            data={rows ?? []}
            columns={columns}
            pagination={{
              paginationState: pagination,
              setPaginationState: setPagination
            }}
            totalRows={totalRowCount}
            loading={isValidating}
            handleRowClick={onRowClick}
            customPaginationConfig={{
              customPagination: false
            }}
            isCustomizable={{
              isFullWidth: false,
              isResizable: false
            }}
          />
        </div>
      )
    }
    return <ViewChannelDetails channelId={channelId} />

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setting_id, rows, isValidating, newpage, newrowsPerPage])

  // ** Alert Handlers ** //
  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (_event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  if (!isValidating && !Boolean(channelsResponse)) return <></>

  return (
    <>
      {renderChannelsList()}
      <FloGenericDialog
        isOpen={isCreateChannelDialogOpen}
        width={400}
        handleClose={setIsCreateChannelDialogOpen}
        jobButtonText={'Create Channel'}
        closeButtonText={'Cancel'}
        content={<>{renderCreateChannelDialogContent()}</>}
        title={`Create a channel`}
        closeButtonVariant='text'
        handleJob={handleCreateChannel}
      />
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={5000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}

export default Channels
